import instance from "api/globalaxios";

export async function getCaseTimeline(id) {
  return await instance({
    method: 'get',
    url: `api/timeline/?id=${id}`,
  });
}

export async function getCaseMandate(disputeId) {
  return await instance.get(`/bac/cases/${disputeId}/mandate/`);
}

export async function getRecordOfProceedings(dispute_id, { offset = 0, limit } = {}) {
  return await instance.get(`/bac/cases/${dispute_id}/activity-summary/`, {
    params: { offset, ...(limit ? { limit } : {}) },
  });
}
export async function startRecordOfProceedings(dispute_id) {
  return await instance.post(`/bac/cases/${dispute_id}/activity-summary/`);
}

export async function searchAvailableRooms(listingDate, stageId) {
  // stageId is optional: adjournment re-scheduling just needs free/booked slots.
  // Omitting it means no "recommended duration" highlighting (backend tolerates).
  const stageParam = stageId ? `&stage_id=${stageId}` : "";
  return await instance.get(`/bac/get-open-rooms/?listing_date=${listingDate}${stageParam}`);
}

export async function bookHearingRoom(payload) {
  return await instance.post("/bac/causlist/", payload);
}

export async function getDirections(disputeId) {
  return await instance.get(`/bac/direction/?dispute_id=${disputeId}`);
}

export async function createDirection(payload) {
  return await instance.post("/bac/direction/", payload);
}

export async function uploadDirectionDocument(directionId, file) {
  const formData = new FormData();
  formData.append("documents", file);
  return await instance.put(`/bac/direction/${directionId}/`, formData, {
    headers: {
      "content-type": "multipart/form-data",
    },
  });
}

export async function getArbitratorDashboard(profileId, params = {}) {
  return await instance.get(`/bac/masters/arbitrator/${profileId}/dashboard/`, { params });
}

export async function getArbitratorsStats() {
  return await instance.get("/bac/arbitrators/stats/");
}

export async function printArbitratorDashboardReport(profileId, params = {}) {
  return await instance.get(`/bac/masters/arbitrator/${profileId}/dashboard/report/`, {
    params,
    responseType: "blob",
  });
}

export async function getScrutinyList(params = {}) {
  return await instance.get("/bac/scrutiny/", { params });
}

export async function getCaseDetails(disputeId) {
  return await instance.get(`/api/tiacrvdisputedetailsbyid/${disputeId}/`);
}

export async function getCaseFiles(disputeId) {
  return await instance.get("/api/casefile/", { params: { dispute__id: disputeId } });
}

export async function getCaseChecklist(caseId) {
  return await instance.get(`/bac/cases/${caseId}/checklist/`);
}

export async function getScrutinyChecklist(scrutinyId) {
  return await instance.get(`/bac/scrutiny/${scrutinyId}/checklist/`);
}

export async function approveScrutinyDocument(id) {
  return await instance.patch(`/bac/scrutiny/${id}/approve/`);
}

export async function markScrutinyDocumentDefective(id, reason) {
  return await instance.patch(`/bac/scrutiny/${id}/mark-defective/`, { reason });
}

export async function reviewScrutinyDocument(id, payload) {
  return await instance.patch(`/bac/scrutiny/${id}/review/`, payload);
}

export async function getScrutinyDocumentPreview(id) {
  return await instance.get(`/bac/scrutiny/${id}/preview/`);
}

export async function getCaseScrutinyDocs(caseId) {
  return await instance.get(`/bac/cases/${caseId}/scrutiny/`);
}

export async function getScrutinyDefectMemo(id) {
  return await instance.get(`/bac/scrutiny/${id}/defect-memo/`, { responseType: "blob" });
}

export async function reuploadScrutinyDocument(id, payload) {
  return await instance.patch(`/bac/scrutiny/${id}/reupload/`, payload);
}

export async function getCalculatedFee(amount, probono, numberOfPrayers) {
  const params = {};
  if (probono !== undefined && probono !== null) {
    params.probono = probono;
  }
  if (
    numberOfPrayers !== undefined &&
    numberOfPrayers !== null &&
    numberOfPrayers !== ""
  ) {
    params.number_of_prayers = numberOfPrayers;
  }
  if (amount !== undefined && amount !== null && amount !== "") {
    const amountStr = amount.toString();
    let cleanAmount = amountStr;
    if (amountStr.includes("e") || amountStr.includes("E")) {
      const parsedNum = Number(amount);
      if (!isNaN(parsedNum)) {
        cleanAmount = parsedNum.toFixed(0).replace(/\.0+$/, "");
      }
    }
    return await instance.get(`/bac/calculate-fee/${cleanAmount}/`, { params });
  }
  return await instance.get("/bac/calculate-fee/", { params });
}

export async function getPreviewNotice(params = {}, fileId = null, config = {}) {
  const url = fileId ? `/bac/preview-notice/${fileId}/` : "/bac/preview-notice/";
  return await instance.get(url, {
    params,
    ...config,
  });
}

export async function updatePreviewNotice(fileId, payload, disputeId, listingDate, noticeType, statementOfClaimFilingDate = null) {
  return await instance.patch(`/bac/preview-notice/${fileId}/`, payload, {
    params: {
      dispute_id: disputeId,
      listing_date: listingDate,
      notice_type: noticeType,
      statement_of_claim_filing_date: statementOfClaimFilingDate || null
    }
  });
}

// Saves the edited notice as a whole document. `content` is the pristine
// letterhead re-joined to the edited body; the backend converts it back to
// .docx and marks the staged file manually_edited.
export async function updatePreviewNoticeDocument(fileId, content) {
  return await instance.post(`/bac/preview-notice/${fileId}/document/`, { content });
}

export async function getFinancialEntries(params = {}) {
  return await instance.get("/bac/financial/", { params });
}

export async function createFinancialEntry(payload, probono) {
  const params = {};
  if (probono !== undefined && probono !== null) {
    params.probono = probono;
  }
  return await instance.post("/bac/financial/", payload, { params });
}

export async function updateFinancialEntry(id, payload, probono) {
  const params = {};
  if (probono !== undefined && probono !== null) {
    params.probono = probono;
  }
  return await instance.patch(`/bac/financial/${id}/`, payload, { params });
}

// ---- Fee calculation entries (Financial > Fee Calculations tab) ----
export async function getFeeCalculations(params = {}) {
  return await instance.get("/bac/financial/fee-calculation/", { params });
}

export async function createFeeCalculation(payload) {
  // { dispute_id, claim_amount, total_amount, number_of_prayers,
  //   prayers: [{ description }] }
  return await instance.post("/bac/financial/fee-calculation/", payload);
}

export async function updateFeeCalculation(payload) {
  // { id, claim_amount, total_amount, amount_paid, received_by,
  //   receipt_date, challan_number }
  return await instance.patch("/bac/financial/fee-calculation/", payload);
}

export async function getAwardParties(disputeId) {
  return await instance.get(`/bac/cases/${disputeId}/award-parties/`);
}

export async function getAdjournments(disputeId, isAdjourn) {
  // isAdjourn: true = Adjournment module, false = Reschedule module.
  // Omitted returns both (back-compat).
  const q = isAdjourn === undefined ? "" : `?is_adjourn=${isAdjourn ? "true" : "false"}`;
  return await instance.get(`/bac/cases/${disputeId}/adjournments/${q}`);
}

export async function createAdjournment(disputeId, payload) {
  // payload: { reason, proposed_date? }
  return await instance.post(`/bac/cases/${disputeId}/adjournments/`, payload);
}

export async function actionAdjournment(disputeId, adjournmentId, payload) {
  // payload: { action: "approved" | "rejected", action_note? }
  return await instance.patch(
    `/bac/cases/${disputeId}/adjournments/${adjournmentId}/action/`,
    payload
  );
}

// ---- Staff access (role -> feature) ----
export async function getMyBACPermissions() {
  return await instance.get("/bac/me/permissions/");
}

export async function getBACStaffRoles() {
  return await instance.get("/bac/staff/roles/");
}

export async function getBACStaffAccess() {
  return await instance.get("/bac/staff/access/");
}

export async function setBACStaffAccess(payload) {
  // { profile_id, role_code, designation?, is_active? }
  return await instance.post("/bac/staff/access/", payload);
}

// ---- Payment challan (online Easebuzz flow) ----
export async function getDisputeParticipants(disputeId) {
  return await instance.get('/bac/parties/', { params: { dispute_id: disputeId } });
}

export async function getPaymentChallans(params = {}) {
  return await instance.get('/bac/payment-challans/', { params });
}

export async function createPaymentChallan(payload) {
  // { dispute_id, sum_in_dispute, total_fee, admin_fee?, arbitrator_fee?,
  //   is_pro_bono?, parties: [{profile_id, amount}] }
  return await instance.post('/bac/payment-challans/', payload);
}

export async function initiatePartyPayment(shareId) {
  return await instance.post(`/bac/payment-challans/initiate/${shareId}/`);
}

export async function getMyPayments(params = {}) {
  return await instance.get('/bac/my-payments/', { params });
}

// Khajane-II (Karnataka treasury) payments.
export async function initiateK2Payment(shareId) {
  return await instance.post(`/bac/k2/pay/${shareId}/`);
}

// { ref, status, result: success|failure|processing, challan, amount, payment_mode, paid_at }
export async function getK2PaymentStatus(ref) {
  return await instance.get(`/bac/k2/status/${encodeURIComponent(ref)}/`);
}

// ---- OCR subscription payment (Razorpay) ----
export const OCR_PAYMENT_ENDPOINTS = {
  createOrder: '/bac/ocr-subscription/order/',
  verifyPayment: '/bac/ocr-subscription/verify/',
};

export async function createOcrSubscriptionOrder(payload) {
  // { amount, dispute_id } -> { success, key, order_id, amount, currency, message }
  return await instance.post(OCR_PAYMENT_ENDPOINTS.createOrder, payload);
}

export async function verifyOcrPayment(payload) {
  // { profile_id, dispute_id, razorpay_payment_id, razorpay_order_id, razorpay_signature }
  return await instance.post(OCR_PAYMENT_ENDPOINTS.verifyPayment, payload);
}

// ---- File upload notification ----
export async function triggerFileUploadEmail(payload) {
  // "tigger" is the backend's spelling of the endpoint, not a typo here.
  return await instance.post('/bac/file-upload-tigger-email/', payload);
}

// ---- Dispute email history ----
export async function getDisputeEmailHistory(caseNumber) {
  return await instance.get("/bac/dispute-email-history/", {
    params: { case_number: caseNumber },
    suppressGlobalErrorHandling: true,
  });
}

export async function getMeetingLinksDashboard(params = {}) {
  return await instance.get("/bac/meeting-link/dashboard/", {
    params,
    suppressGlobalErrorHandling: true,
  });
}

export async function downloadFeeCalculationReport(id, breakdown = {}) {
  return await instance.post(
    "/bac/financial/fee-calculation/generate/",
    { id, ...breakdown },
    { responseType: "blob" }
  );
}

export async function getFactsheetHtml(fileId) {
  return await instance.get(`/bac/factsheet/${fileId}/html/`, {
    suppressGlobalErrorHandling: true,
  });
}

export async function updateFactsheetHtml(fileId, content) {
  return await instance.put(
    `/bac/factsheet/${fileId}/html/`,
    { content },
    { suppressGlobalErrorHandling: true }
  );
}

export async function getBacClosureReasons() {
  return await instance.get("/bac/closure-reasons/");
}

export async function reopenDispute(disputeId, disputeName, description, reopenReason) {
  return await instance.put(
    `/api/dispute/${disputeId}/`,
    {
      id: disputeId,
      reopened: true,
      name: disputeName,
      description,
      reopen_reason: reopenReason,
    },
    { suppressGlobalErrorHandling: true }
  );
}

export async function createGoogleMeeting(disputeId) {
  return await instance.post("/creategooglemeeting/", {
    dispute_id: disputeId,
  });
}


export async function getOrderShortcutMasters(params) {
  // params: { search?: "claim", is_active?: "true" | "false" }
  return await instance.get(`/bac/masters/order-shortcuts/`, {
    params,
    suppressGlobalErrorHandling: true,
  });
}

export async function createOrderShortcut(body) {
  // body: { abbreviation, expansion, is_active?, order? }
  return await instance.post(`/bac/masters/order-shortcuts/`, body, {
    suppressGlobalErrorHandling: true,
  });
}

export async function updateOrderShortcut(id, body) {
  // body: any of { abbreviation, expansion, is_active, order }
  return await instance.patch(`/bac/masters/order-shortcuts/${id}/`, body, {
    suppressGlobalErrorHandling: true,
  });
}

export async function deleteOrderShortcut(id) {
  return await instance.delete(`/bac/masters/order-shortcuts/${id}/`, {
    suppressGlobalErrorHandling: true,
  });
}
